import React from 'react';

/**
 * ChunkErrorBoundary
 * ------------------
 * Fängt Ladefehler ab, die nach einem Deployment auftreten, wenn der
 * Browser noch alte JS-Chunk-Hashes zu laden versucht (z. B. beim
 * Öffnen eines länger inaktiven Tabs oder beim Route-Wechsel).
 *
 * Erkannt werden:
 *   - `ChunkLoadError` (Webpack / CRA)
 *   - `Loading chunk … failed` / `Loading CSS chunk … failed`
 *   - `Failed to fetch dynamically imported module …` (Vite/ESBuild)
 *
 * Verhalten:
 *   - Beim ersten Erkennen ein einmaliges `window.location.reload()`
 *     (Session-Flag verhindert Endlos-Loops, falls das echte Problem
 *     nicht ein alter Chunk sondern ein Runtime-Fehler ist).
 *   - Zeigt in der Zwischenzeit einen dezenten Loading-Screen an
 *     statt eines weißen Bildschirms.
 *   - Reagiert zusätzlich auf globale unhandled-promise-rejection und
 *     window.onerror-Events (z. B. verspätete lazy-import Fehler, die
 *     der React-Boundary nicht auffängt).
 */
const CHUNK_ERROR_RE = /Loading (?:CSS )?chunk [^ ]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i;
const RELOAD_FLAG_KEY = 'chunk_error_reload_ts';
const RELOAD_MIN_INTERVAL_MS = 15_000; // maximal 1 Reload pro 15s

const isChunkError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = typeof err === 'string' ? err : (err.message || '');
  return CHUNK_ERROR_RE.test(msg);
};

const scheduleReload = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG_KEY) || '0');
    const now = Date.now();
    if (now - last < RELOAD_MIN_INTERVAL_MS) return false;
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(now));
  } catch (_) { /* Private mode: einfach neuladen */ }
  // Cache-Bypass forcieren: window.location.reload(true) ist deprecated,
  // stattdessen einen frischen href setzen mit Cache-Buster.
  const url = new URL(window.location.href);
  url.searchParams.set('_rl', String(Date.now()));
  window.location.replace(url.toString());
  return true;
};

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasChunkError: false };
    this._onUnhandledRejection = this._onUnhandledRejection.bind(this);
    this._onWindowError = this._onWindowError.bind(this);
  }

  static getDerivedStateFromError(error) {
    if (isChunkError(error)) {
      return { hasChunkError: true };
    }
    // Andere Fehler unverändert nach oben durchreichen
    throw error;
  }

  componentDidCatch(error, info) {
    if (isChunkError(error)) {
      // eslint-disable-next-line no-console
      console.warn('[ChunkErrorBoundary] chunk load failed, forcing reload', error);
      scheduleReload();
    }
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this._onUnhandledRejection);
    window.addEventListener('error', this._onWindowError, true);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this._onUnhandledRejection);
    window.removeEventListener('error', this._onWindowError, true);
  }

  _onUnhandledRejection(evt) {
    if (isChunkError(evt?.reason)) {
      // eslint-disable-next-line no-console
      console.warn('[ChunkErrorBoundary] unhandled chunk-load rejection', evt.reason);
      evt.preventDefault?.();
      this.setState({ hasChunkError: true });
      scheduleReload();
    }
  }

  _onWindowError(evt) {
    // window.onerror kann sowohl ErrorEvent als auch simple Nachrichten liefern
    const err = evt?.error || evt?.message;
    if (isChunkError(err)) {
      // eslint-disable-next-line no-console
      console.warn('[ChunkErrorBoundary] window error chunk-load', err);
      evt.preventDefault?.();
      this.setState({ hasChunkError: true });
      scheduleReload();
    }
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div
          role="alert"
          aria-live="polite"
          data-testid="chunk-error-boundary-loading"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f0f9ff',
            color: '#075985',
            fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            padding: '24px',
            textAlign: 'center',
            zIndex: 99999,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: '3px solid rgba(2, 132, 199, 0.25)',
              borderTopColor: '#0369a1',
              animation: 'chunk-boundary-spin 0.9s linear infinite',
              marginBottom: 16,
            }}
          />
          <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Neue Version wird geladen…</p>
          <p style={{ fontSize: 13, marginTop: 6, color: '#0c4a6e', opacity: 0.75 }}>
            Bitte einen Moment Geduld, wir aktualisieren die Seite automatisch.
          </p>
          <style>{`@keyframes chunk-boundary-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
